import React, { useEffect, useRef, useState } from "react";
import { Map, useMap, useMapsLibrary, AdvancedMarker, Pin } from "@vis.gl/react-google-maps";
import { Loader2, Route, Clock, AlertCircle } from "lucide-react";

// Inner directions renderer — must be a child of <Map>
const Directions = ({ pickup, drop, onRoute }) => {
  const map = useMap();
  const routesLib = useMapsLibrary("routes");
  const [directionsService, setDirectionsService] = useState(null);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);
  const [routeError, setRouteError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!routesLib || !map) return;
    setDirectionsService(new routesLib.DirectionsService());
    const renderer = new routesLib.DirectionsRenderer({
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: "#F5C518",
        strokeOpacity: 0.95,
        strokeWeight: 5,
      },
    });
    renderer.setMap(map);
    setDirectionsRenderer(renderer);
    return () => renderer.setMap(null);
  }, [routesLib, map]);

  useEffect(() => {
    if (!directionsService || !directionsRenderer) return;
    if (!pickup?.location || !drop?.location) return;
    setLoading(true);
    setRouteError("");
    directionsService
      .route({
        origin: pickup.location,
        destination: drop.location,
        travelMode: "DRIVING",
      })
      .then((response) => {
        directionsRenderer.setDirections(response);
        const leg = response.routes?.[0]?.legs?.[0];
        if (leg && onRoute) {
          onRoute({
            distanceText: leg.distance?.text,
            distanceValue: leg.distance?.value,
            durationText: leg.duration?.text,
            durationValue: leg.duration?.value,
          });
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Directions error", err);
        setRouteError("Could not compute route between these locations");
        setLoading(false);
        onRoute?.(null);
      });
  }, [directionsService, directionsRenderer, pickup, drop, onRoute]);

  if (loading) {
    return (
      <div className="absolute top-3 left-3 bg-white/95 backdrop-blur rounded-full px-3 py-1.5 shadow-md flex items-center gap-2 text-xs font-semibold text-[#0B1120]">
        <Loader2 className="h-3 w-3 animate-spin" /> Calculating route…
      </div>
    );
  }

  if (routeError) {
    return (
      <div className="absolute top-3 left-3 bg-red-50 border border-red-200 text-red-700 rounded-full px-3 py-1.5 shadow-md flex items-center gap-2 text-xs font-semibold">
        <AlertCircle className="h-3 w-3" /> {routeError}
      </div>
    );
  }

  return null;
};

const RoutePreview = ({ pickup, drop, route, onRoute }) => {
  // initial center on India if no pickup yet
  const center = pickup?.location || { lat: 22.9734, lng: 78.6569 };
  const zoom = pickup?.location && drop?.location ? 7 : pickup?.location ? 12 : 5;

  return (
    <div
      data-testid="route-preview-section"
      className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-xl bg-[#F8FAFC]"
    >
      <div className="h-[280px] sm:h-[320px] lg:h-[360px] w-full">
        <Map
          mapId="cabsab-light"
          defaultCenter={center}
          defaultZoom={zoom}
          gestureHandling="cooperative"
          disableDefaultUI={true}
          zoomControl
          mapTypeControl={false}
          fullscreenControl={false}
          streetViewControl={false}
          clickableIcons={false}
          style={{ width: "100%", height: "100%" }}
        >
          {pickup?.location && (
            <AdvancedMarker position={pickup.location}>
              <Pin background="#0B1120" borderColor="#F5C518" glyphColor="#F5C518" />
            </AdvancedMarker>
          )}
          {drop?.location && (
            <AdvancedMarker position={drop.location}>
              <Pin background="#F5C518" borderColor="#0B1120" glyphColor="#0B1120" />
            </AdvancedMarker>
          )}
          <Directions pickup={pickup} drop={drop} onRoute={onRoute} />
        </Map>
      </div>

      {/* Route summary footer */}
      <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-3 bg-white border-t border-slate-100">
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-[#0B1120] ring-2 ring-[#F5C518]"></span>
            <span className="text-slate-500 truncate max-w-[140px] lg:max-w-[200px]">{pickup?.description || "Pickup"}</span>
          </div>
          <span className="text-slate-300">→</span>
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-[#F5C518] ring-2 ring-[#0B1120]"></span>
            <span className="text-slate-500 truncate max-w-[140px] lg:max-w-[200px]">{drop?.description || "Drop"}</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div
            data-testid="route-distance"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B1120]"
          >
            <Route className="h-4 w-4 text-[#F5C518]" />
            {route?.distanceText || "—"}
          </div>
          <div
            data-testid="route-duration"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B1120]"
          >
            <Clock className="h-4 w-4 text-[#F5C518]" />
            {route?.durationText || "—"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoutePreview;
